import axios from "axios";
import { API_ROUTE, Routes } from "./Routes";

export interface ClientLogContext {
  eventAction?: string;
  subjectIdStr?: string;
  statusCode?: number;
  traceId?: string;
  requestPath?: string;
  responseBody?: string;
  payloadSummary?: string;
  errorCode?: string;
}

const truncate = (value: string, maxLength = 1500): string =>
  value.length <= maxLength ? value : `${value.slice(0, maxLength)}...[truncated]`;

const stringifyUnknown = (value: unknown): string => {
  if (typeof value === "string") {
    return value;
  }

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const normalizeClientError = (error: unknown): { description: string; responseBody?: string; errorCode?: string } => {
  if (error instanceof Error) {
    const description = `${error.name}: ${error.message}`;
    const responseBody = typeof error.stack === "string" && error.stack.trim() !== ""
      ? truncate(error.stack)
      : undefined;
    const errorCode = "code" in error && error.code != null
      ? String(error.code)
      : undefined;

    return {
      description: truncate(description, 400),
      responseBody,
      errorCode
    };
  }

  if (typeof error === "string") {
    return { description: truncate(error, 400) };
  }

  const description = truncate(stringifyUnknown(error), 400);
  return { description };
};

const getCookieValue = (name: string): string | null => {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[2]) : null;
};

const refreshLogAccessToken = async (): Promise<string | null> => {
  try {
    const response = await axios.get(`${API_ROUTE}${Routes.REFRESH_TOKEN}`, {
      withCredentials: true
    });
    const refreshedToken = response.data?.accessToken;

    if (typeof refreshedToken === "string" && refreshedToken.trim() !== "") {
      sessionStorage.setItem("accessToken", refreshedToken);
      sessionStorage.setItem("tokenUpdatedOn", new Date().toUTCString());
      return refreshedToken;
    }
  } catch {
    return null;
  }

  return null;
};

const postClientLog = async (accessToken: string, payload: Record<string, unknown>) =>
  axios.post(
    `${API_ROUTE}${Routes.LOG_ERROR}`,
    payload,
    {
      withCredentials: true,
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    }
  );

export function writeLog(
  eventType: string,
  eventDescription: string,
  context: ClientLogContext = {}
) {
  const payload = {
    eventType,
    eventAction: context.eventAction,
    eventDescription,
    eventUserName: getCookieValue("email") ?? undefined,
    subjectIdStr: context.subjectIdStr,
    ...context
  };

  void (async () => {
    let accessToken = sessionStorage.getItem("accessToken");
    if (!accessToken) {
      accessToken = await refreshLogAccessToken();
    }

    if (!accessToken) {
      return;
    }

    try {
      await postClientLog(accessToken, payload);
      return;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        const refreshedToken = await refreshLogAccessToken();
        if (refreshedToken) {
          await postClientLog(refreshedToken, payload);
          return;
        }
      }

      throw error;
    }
  })().catch((error: unknown) => {
    const failure = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
    console.error("Client log delivery failed", failure);
  });
}

export function writeClientError(
  eventAction: string,
  error: unknown,
  context: ClientLogContext = {}
) {
  const normalized = normalizeClientError(error);

  writeLog("UI_ERR", normalized.description, {
    ...context,
    eventAction,
    requestPath: context.requestPath ?? window.location.pathname,
    responseBody: context.responseBody ?? normalized.responseBody,
    errorCode: context.errorCode ?? normalized.errorCode
  });
}
